import { useState } from 'react';
import { Button } from '../ui/button';

/**
 * ICOGuidance
 * -----------
 * Appears only when complaining to the ICO is a realistic next step:
 *   - the deadline has passed without the records
 *   - the organisation refused
 *   - the user has flagged redactions
 *
 * `open` / `onOpenChange` are optional; when the parent passes them the
 * steps can be opened from the deadline panel's "How to complain" button.
 */
const longDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
};

const ICOGuidance = ({ sar, deadline, open, onOpenChange, id = 'ico-guidance' }) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const isOpen = typeof open === 'boolean' ? open : internalOpen;
  const setOpen = (v) => {
    setInternalOpen(v);
    onOpenChange?.(v);
  };

  if (!sar) return null;

  const reasons = [];
  if (sar.refused_at) reasons.push('refusal');
  if (sar.redactions_flagged) reasons.push('redactions');
  const rawDeadline = deadline?.extended_deadline || deadline?.initial_deadline;
  const deadlineDate = rawDeadline ? new Date(rawDeadline) : null;
  if (deadlineDate && deadlineDate < new Date() && sar.status !== 'complete' && !sar.refused_at) {
    reasons.push('overdue');
  }
  if (reasons.length === 0) return null;

  const headline = reasons.includes('overdue')
    ? 'You can complain to the ICO about the delay'
    : reasons.includes('refusal')
      ? 'You can complain to the ICO about the refusal'
      : 'You can complain to the ICO about the redactions';

  return (
    <section id={id} className="rounded-lg bg-ground px-6 py-5" data-testid="ico-guidance">
      <h2 className="font-heading text-[19px] font-semibold tracking-[-0.02em] text-ink">{headline}</h2>
      <p className="mt-2 text-[15px] leading-[1.6] text-ink-70 max-w-[60ch]">
        The Information Commissioner&rsquo;s Office (ICO) is the regulator for data protection in the UK.
        Complaining is free and you do not need a solicitor. They usually expect you to have raised it
        with the organisation first, which you can do from this page.
      </p>

      <div className="mt-4 flex flex-wrap gap-2.5">
        <Button asChild variant="secondary">
          <a
            href="https://ico.org.uk/make-a-complaint/data-protection-complaints/"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="ico-guidance-external-link"
          >
            Open the ICO complaints page
          </a>
        </Button>
        <Button
          variant="ghost"
          onClick={() => setOpen(!isOpen)}
          aria-expanded={isOpen}
          aria-controls={`${id}-steps`}
          data-testid="ico-guidance-toggle"
        >
          {isOpen ? 'Hide the steps' : 'How to complain, in three steps'}
        </Button>
      </div>

      {isOpen && (
        <ol id={`${id}-steps`} className="mt-5 flex flex-col gap-4 border-t border-rule pt-5">
          <li className="flex gap-3">
            <span className="pd-mono mt-1 w-6 shrink-0 text-ink-55">1</span>
            <div>
              <p className="text-[15px] font-semibold text-ink">Raise it with the organisation first</p>
              <p className="mt-1 text-[15px] leading-[1.6] text-ink-70">
                Reply to them from this page. Say what has gone wrong and ask for a written answer
                within 14 days. Everything you send and receive here is kept, and the ICO will ask
                to see it.
              </p>
            </div>
          </li>
          <li className="flex gap-3">
            <span className="pd-mono mt-1 w-6 shrink-0 text-ink-55">2</span>
            <div>
              <p className="text-[15px] font-semibold text-ink">Have these ready</p>
              <ul className="mt-1 list-disc pl-5 text-[15px] leading-[1.6] text-ink-70">
                <li>
                  The date you sent the request
                  {sar.sent_at && <span className="pd-mono ml-2 text-ink-55">{longDate(sar.sent_at).toUpperCase()}</span>}
                </li>
                <li>Copies of every reply from the organisation</li>
                <li>A few sentences on what went wrong and why the response was not good enough</li>
              </ul>
            </div>
          </li>
          <li className="flex gap-3">
            <span className="pd-mono mt-1 w-6 shrink-0 text-ink-55">3</span>
            <div>
              <p className="text-[15px] font-semibold text-ink">Fill in the ICO&rsquo;s online form</p>
              <p className="mt-1 text-[15px] leading-[1.6] text-ink-70">
                Use the link above. There is no fee. When it asks whether you have contacted the
                organisation, say yes and attach the emails. You can download everything on this
                request from the export tools to attach.
              </p>
            </div>
          </li>
        </ol>
      )}
    </section>
  );
};

export default ICOGuidance;
